/**
 * Schémas Zod — Tenants & Users (admin tenant).
 *
 * Les routes /v1/tenants (super_admin only) consomment ces schémas pour
 * la gestion des cabinets de courtage. Le filtre listing supporte une
 * recherche par nom ou slug, et un toggle pour inclure les tenants en
 * statut `cancelled` ou `suspended`.
 */

import { z } from "zod";

import { USER_ROLES } from "../constants.js";

import { EmailSchema, LocaleSchema, PhoneSchema, SlugSchema } from "./common.js";

export const AddressSchema = z.object({
  street: z.string().max(200).optional(),
  city: z.string().max(100).optional(),
  province: z.string().length(2).optional(),
  postal_code: z.string().max(10).optional(),
  country: z.string().length(2).default("CA"),
});

// Branding tenant : couleurs CSS hex + URL logo. Whitelist stricte des clés
// pour éviter qu'un PATCH n'injecte n'importe quoi dans la colonne JSONB.
// Les noms matchent les variables CSS exposées par Chunk 7.A (--brand-*).
const HexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Couleur hex 6-digits attendue (ex. #0066cc)");

export const BrandingSchema = z
  .object({
    primary: HexColorSchema.optional(),
    primary_foreground: HexColorSchema.optional(),
    bg: HexColorSchema.optional(),
    surface: HexColorSchema.optional(),
    border: HexColorSchema.optional(),
    text: HexColorSchema.optional(),
    logo_url: z.string().url().max(500).optional(),
  })
  .strict();
export type BrandingInput = z.infer<typeof BrandingSchema>;

/** Complete replacement: omitted flags never silently reset a cabinet policy. */
export const QuoteDeliveryPolicySchema = z
  .object({
    require_census: z.boolean(),
    include_signed_mandate: z.boolean(),
    require_commission: z.boolean(),
  })
  .strict();
export type QuoteDeliveryPolicy = z.infer<typeof QuoteDeliveryPolicySchema>;

export const CreateTenantSchema = z.object({
  slug: SlugSchema,
  name: z.string().min(2).max(200),
  legal_name: z.string().max(200).optional(),
  email: EmailSchema.optional(),
  phone: PhoneSchema.optional(),
  address: AddressSchema.optional(),
  locale: LocaleSchema,
  plan: z.enum(["trial", "starter", "pro", "enterprise"]).default("trial"),
});
export type CreateTenantInput = z.infer<typeof CreateTenantSchema>;

// Update super_admin : tous les champs métier optionnels SAUF slug (immuable une
// fois créé, car utilisé dans les URLs et potentiellement pour la résolution
// de tenant par sous-domaine plus tard).
export const UpdateTenantSchema = CreateTenantSchema.partial()
  .omit({ slug: true })
  .extend({
    // Champs admin pas exposés à la création — un super_admin peut les ajuster.
    status: z.enum(["trial", "active", "past_due", "suspended", "cancelled"]).optional(),
    trial_ends_at: z.string().datetime().nullable().optional(),
    max_users: z.number().int().min(1).max(10_000).optional(),
    max_projects: z.number().int().min(1).max(100_000).optional(),
    max_storage_mb: z.number().int().min(100).max(1_000_000).optional(),
    // Présentation
    logo_url: z.string().url().max(500).nullable().optional(),
    timezone: z.string().min(2).max(64).optional(),
    currency: z.string().length(3).optional(),
    branding: BrandingSchema.nullable().optional(),
    quote_delivery_policy: QuoteDeliveryPolicySchema.optional(),
  });
export type UpdateTenantInput = z.infer<typeof UpdateTenantSchema>;

// Update self-service tenant_admin : whitelist serrée. **Aucun** champ admin
// SaaS (plan, status, trial_ends_at, max_*) ni champ identitaire structurel
// (slug déjà omit, name reste éditable car c'est le nom du cabinet).
// Ce schéma est sélectionné côté route en fonction du rôle applicatif.
export const UpdateTenantSelfSchema = z
  .object({
    name: z.string().min(2).max(200).optional(),
    legal_name: z.string().max(200).nullable().optional(),
    email: EmailSchema.nullable().optional(),
    phone: PhoneSchema.nullable().optional(),
    address: AddressSchema.nullable().optional(),
    locale: LocaleSchema.optional(),
    logo_url: z.string().url().max(500).nullable().optional(),
    timezone: z.string().min(2).max(64).optional(),
    currency: z.string().length(3).optional(),
    branding: BrandingSchema.nullable().optional(),
    quote_delivery_policy: QuoteDeliveryPolicySchema.optional(),
  })
  .strict();
export type UpdateTenantSelfInput = z.infer<typeof UpdateTenantSelfSchema>;

export const TenantFilterSchema = z.object({
  q: z.string().max(100).optional(),
  status: z.enum(["trial", "active", "past_due", "suspended", "cancelled", "all"]).default("all"),
  plan: z.enum(["trial", "starter", "pro", "enterprise", "all"]).default("all"),
  include_deleted: z.coerce.boolean().default(false),
});
export type TenantFilter = z.infer<typeof TenantFilterSchema>;

export const UpdateUserProfileLegacySchema = z.object({
  full_name: z.string().min(2).max(120).optional(),
  phone: PhoneSchema.optional(),
  locale: LocaleSchema.optional(),
  avatar_url: z.string().url().optional(),
  role: z.enum(USER_ROLES).optional(), // tenant_admin only
});
export type UpdateUserProfileLegacyInput = z.infer<typeof UpdateUserProfileLegacySchema>;

export const FeatureFlagSchema = z.object({
  flag_key: z.string().min(2).max(50),
  enabled: z.boolean(),
  config: z.record(z.unknown()).optional(),
});
export type FeatureFlagInput = z.infer<typeof FeatureFlagSchema>;
